import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29564" className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-semibold tracking-wide text-foreground/90 transition-colors hover:text-primary focus:text-primary bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent">
                {item.label}
                <span className="absolute inset-x-0 -bottom-1 h-[2px] scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-semibold text-foreground/90 transition-colors hover:text-primary" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <Button asChild className="mt-4 w-full rounded-[4px] border-2 border-primary bg-primary text-primary-foreground font-semibold hover:bg-transparent hover:text-primary transition-all" onClick={onClose}>
        <Link to="/ContactPage">List Your Brand</Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("light sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300", isScrolled ? "bg-white/80 backdrop-blur-md border-border/10 shadow-md" : "bg-white")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <Link to="/HomePage" className="flex items-center gap-2 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577657/img/business_tantra_logo.png" alt="Business Tantra Logo" className="h-8 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="39">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:block">
          <Button asChild className="rounded-[4px] border-2 border-primary bg-primary px-6 font-semibold text-primary-foreground shadow-sm transition-all hover:bg-transparent hover:text-primary hover:shadow-md">
            <Link to="/ContactPage">List Your Brand</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="z-50 flex h-10 w-10 items-center justify-center rounded-md text-foreground md:hidden hover:bg-accent/20" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>

        {}
        <div className={cn("fixed inset-0 z-40 flex flex-col bg-white pt-24 transition-transform duration-300 md:hidden", isMobileMenuOpen ? "translate-x-0" : "translate-x-full")}>
          <MenuProvider menu_id="39">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}